import React, { useEffect, useState } from "react";
import { Card, ConfirmModal } from "../shared";
import { capitalApi } from "../../lib/api";
import * as m from "../../lib/model";
import { toast } from "sonner";
import { CalendarRange, Sparkles, RefreshCcw, Trash2 } from "lucide-react";
import { TimelineWeek } from "./TimelineWeek";

const iso = (d) => d.toISOString().slice(0, 10);
const plusWeeks = (n) => iso(new Date(Date.now() + n * 7 * 864e5));

export const paceFor = (tl, target, weekN) => (tl && target > 0 && tl.weekCount ? (target * Math.min(weekN, tl.weekCount)) / tl.weekCount : 0);

const LABELS = {
  raise: { setup: "Raise timeline — set your target close", start: "Raise start", close: "Target close", toClose: "to target close", pastClose: "target close",
    raised: "Raised to date", pace: "Pace target", planTitle: "Weekly plan", generate: "The generator lays out kickoff → teaser outreach → intro calls & diligence → soft circle → sub docs & wires → close across the weeks you have. Every week is editable afterwards." },
  debt: { setup: "Debt closing timeline — from term sheet to funded", start: "Lender outreach start", close: "Target funding date", toClose: "to funding", pastClose: "funding date",
    raised: "Committed to date", pace: "Pace target", planTitle: "Weekly closing plan", generate: "The generator lays out lender package → outreach & term sheets → select & negotiate → underwriting → commitment & loan docs → closing & funding across the weeks you have. Every week is editable afterwards." },
};

export const TimelineSetup = ({ start, close, setStart, setClose, onGenerate, onBlank, busy, L = LABELS.raise }) => (
  <Card title={L.setup} testid="tl-setup">
    <div className="tl-setup">
      <div className="field"><label>{L.start}</label><input type="date" className="inp" value={start} onChange={(e) => setStart(e.target.value)} data-testid="tl-start" /></div>
      <div className="field"><label>{L.close}</label><input type="date" className="inp" value={close} onChange={(e) => setClose(e.target.value)} data-testid="tl-close" /></div>
      <div className="tl-setup-btns">
        <button className="btn btn-primary" onClick={onGenerate} disabled={busy} data-testid="tl-generate"><Sparkles size={13} />{busy ? "Building…" : "Generate weekly plan"}</button>
        <button className="btn" onClick={onBlank} disabled={busy} data-testid="tl-blank">Start blank</button>
      </div>
    </div>
    <div className="cm-empty">{L.generate}</div>
  </Card>
);

export const RaiseTimeline = ({ dealKey, dealName, target, tl, onChange, kind = "raise" }) => {
  const L = LABELS[kind] || LABELS.raise;
  const [start, setStart] = useState(iso(new Date()));
  const [close, setClose] = useState(plusWeeks(10));
  const [open, setOpen] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const scope = { dealKey, dealName, kind };

  useEffect(() => { if (tl) { setStart(tl.startDate); setClose(tl.closeDate); if (open === undefined) setOpen((tl.weeks.find((w) => w.current) || tl.weeks[0])?.n ?? null); } }, [tl]); // eslint-disable-line

  const act = async (fn, ok) => {
    setBusy(true);
    try { onChange(await fn()); if (ok) toast.success(ok); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save the timeline"); }
    finally { setBusy(false); }
  };
  const generate = () => act(() => capitalApi.generateTimeline({ ...scope, startDate: start, closeDate: close }), "Weekly plan generated");
  const blank = () => act(() => capitalApi.saveTimeline({ ...scope, startDate: start, closeDate: close, weeks: rebucket([], start, close) }), "Timeline created");
  const save = (weeks) => act(() => capitalApi.saveTimeline({ ...scope, startDate: tl.startDate, closeDate: tl.closeDate, weeks: weeks.map(strip) }));
  const applyDates = () => act(() => capitalApi.saveTimeline({ ...scope, startDate: start, closeDate: close, weeks: rebucket(tl.weeks.map(strip), start, close) }), "Dates updated — weeks re-bucketed");
  const remove = () => act(async () => { await capitalApi.deleteTimeline(scope); return null; }, "Timeline removed");

  if (!tl) return <TimelineSetup start={start} close={close} setStart={setStart} setClose={setClose} onGenerate={generate} onBlank={blank} busy={busy} L={L} />;

  const paceNow = paceFor(tl, target, tl.weeksElapsed);
  const delta = tl.totalRaised - paceNow;
  const datesDirty = start !== tl.startDate || close !== tl.closeDate;
  const dtc = tl.daysToClose;

  return (
    <>
      <div className="tl-head" data-testid="tl-head">
        <div className="tl-big"><span className={`tl-days ${dtc < 0 ? "neg" : dtc <= 14 ? "warn" : ""}`}>{dtc < 0 ? `${-dtc}d past` : `${dtc}d`}</span><small>{dtc < 0 ? L.pastClose : L.toClose}</small></div>
        <div className="tl-kpis">
          <div><span>Week</span><b>{Math.min(tl.weeksElapsed || 1, tl.weekCount)} / {tl.weekCount}</b></div>
          <div><span>Activities done</span><b>{tl.tasksDone} / {tl.tasksTotal}</b></div>
          <div><span>{L.raised}</span><b className="pos">{m.fmt$(tl.totalRaised)}</b></div>
          <div><span>{L.pace}</span><b>{target > 0 ? m.fmt$(paceNow) : "—"}</b></div>
          <div><span>vs plan</span><b className={delta >= 0 ? "pos" : "neg"} data-testid="tl-delta">{target > 0 ? `${delta >= 0 ? "+" : "−"}${m.fmt$(Math.abs(delta))}` : "—"}</b></div>
        </div>
        <div className="tl-dates-edit">
          <input type="date" className="inp" value={start} onChange={(e) => setStart(e.target.value)} data-testid="tl-start" />
          <span>→</span>
          <input type="date" className="inp" value={close} onChange={(e) => setClose(e.target.value)} data-testid="tl-close" />
          {datesDirty && <button className="btn btn-primary" onClick={applyDates} disabled={busy} data-testid="tl-apply-dates"><CalendarRange size={12} />Apply</button>}
          <button className="btn" onClick={() => setConfirm("regen")} disabled={busy} title="Rebuild the default plan for these dates" data-testid="tl-regenerate"><RefreshCcw size={12} />Regenerate</button>
          <button className="btn" onClick={() => setConfirm("delete")} disabled={busy} title="Remove timeline" data-testid="tl-delete"><Trash2 size={12} /></button>
        </div>
      </div>

      <Card title={`${L.planTitle} — ${tl.weekCount} week${tl.weekCount === 1 ? "" : "s"} · bar = cumulative ${kind === "debt" ? "committed" : "raised"}, tick = pace to plan`} testid="tl-weeks">
        {tl.weeks.map((w) => (
          <TimelineWeek key={w.n} week={w} target={target} pace={paceFor(tl, target, w.n)} open={open === w.n}
            onToggle={() => setOpen(open === w.n ? null : w.n)}
            onChange={(nw) => save(tl.weeks.map((x) => (x.n === nw.n ? nw : x)))} kind={kind} />
        ))}
      </Card>

      <ConfirmModal open={!!confirm} title={confirm === "delete" ? "Remove timeline" : "Regenerate weekly plan"} danger confirmLabel={confirm === "delete" ? "Remove" : "Regenerate"}
        message={confirm === "delete" ? "Delete the raise timeline for this deal? Your weekly activities will be lost." : "Replace every week's activities with the default plan for the selected dates? Ticked items will be lost."}
        onConfirm={() => { (confirm === "delete" ? remove() : generate()); setConfirm(null); }} onCancel={() => setConfirm(null)} />
    </>
  );
};

const strip = ({ n, start, end, title, tasks }) => ({ n, start, end, title, tasks: tasks.map(({ id, text, done }) => ({ id, text, done })) });

const rebucket = (weeks, start, close) => {
  const d0 = new Date(start + "T00:00:00"), d1 = new Date(close + "T00:00:00");
  const days = Math.round((d1 - d0) / 864e5);
  const n = Math.max(1, Math.min(52, Math.ceil((days + 1) / 7)));
  const last = weeks.length > 1 ? weeks[weeks.length - 1] : null;
  const body = last ? weeks.slice(0, -1) : weeks;
  return Array.from({ length: n }, (_, i) => {
    const ws = new Date(d0.getTime() + i * 7 * 864e5);
    const we = new Date(Math.min(ws.getTime() + 6 * 864e5, d1.getTime()));
    const prev = last && i === n - 1 ? last : body[i];
    return { n: i + 1, start: iso(ws), end: iso(we), title: prev?.title || "", tasks: prev?.tasks || [] };
  });
};
